<script setup lang="ts">
import { computed, ref } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import { useI18n } from 'vue-i18n'

import { useMutation } from '@/composables/api'
import { useDialogsStore } from '@/stores/dialogsStore'
import { invalidateCaseResources } from '@/utils/queryInvalidation'

import BDialog from '@/components/base/BDialog/BDialog.vue'
import RespiteDatePage from './RespiteDatePage.vue'
import RespiteFeedbackPage from './RespiteFeedbackPage.vue'

// Singleton respite ("postpone payment") dialog (ADA-4437 etapp 4) — replaces @old respite.
// Opens via dialogsStore.respite (written by useDebtActions.openPostpone since etapp 6).
// Two windows: date picker → success/failed feedback. On success we invalidate
// the overview case resources explicitly (domain-action paths are NOT covered by the global
// auto-invalidation — mirrors ada-app-ui's per-action pattern). The legacy in-place mutation of
// document.next_flow_item is deliberately NOT ported (fixes R14). NB: the card/detail "due date"
// itself comes from the demand letter, which respite does not move — that display is ADA-4434.
const { t } = useI18n()
const dialogsStore = useDialogsStore()
const queryClient = useQueryClient()

const model = computed({
  get: () => dialogsStore.respite != null,
  set: (value) => {
    if (!value) dialogsStore.respite = null
  },
})

const page = ref<'date' | 'feedback'>('date')
const feedback = ref<'success' | 'failed' | null>(null)
const chosenDate = ref<Date | null>(null)

const respiteMutation = useMutation('post', '/domain-actions/cases/respite/{resource_id}', {
  // The feedback page IS the success message.
  meta: { notifyOnSuccess: false },
})

async function confirm(payload: { days: number; date: Date }) {
  if (dialogsStore.respite == null) return
  chosenDate.value = payload.date
  try {
    await respiteMutation.mutateAsync({
      path: { resource_id: dialogsStore.respite.params.id },
      // @ts-expect-error Swagger lies — the respite action requires a { days } body.
      body: { days: payload.days },
    })
    feedback.value = 'success'
  } catch {
    // The global error snackbar is emitted by the vue-query plugin.
    feedback.value = 'failed'
  }
  page.value = 'feedback'
  if (feedback.value !== 'success') return

  // Fire-and-forget OUTSIDE the try: the respite already succeeded — a failing refetch must
  // not flip the feedback to 'failed' (query errors snackbar via the global handlers).
  // A respite also moves a pending autogiro withdrawal (+days, serverside), so the D10 rows
  // must refetch too — same extraPrefixes pattern as SAmortizationDialog/RefundDialog.
  void invalidateCaseResources(queryClient, ['/customer--direct-debit-payments'])
}

function afterLeave() {
  page.value = 'date'
  feedback.value = null
  chosenDate.value = null
}
</script>

<template>
  <b-dialog
    v-model="model"
    :hide-footer="true"
    @after-leave="afterLeave"
  >
    <template #title>{{ t('common.postpone_your_payment') }}</template>

    <!-- `disabled` turns off VWindow's user-driven navigation (touch swipe) — the steps advance
         only programmatically, unlike the swipeable Home tabs. -->
    <v-window
      v-model="page"
      disabled
    >
      <v-window-item value="date">
        <respite-date-page
          v-if="dialogsStore.respite != null"
          :document="dialogsStore.respite.document"
          :params="dialogsStore.respite.params"
          :ocr="dialogsStore.respite.ocr"
          :loading="respiteMutation.isPending.value"
          @cancel="model = false"
          @confirm="confirm"
        />
      </v-window-item>

      <v-window-item value="feedback">
        <respite-feedback-page
          :feedback="feedback"
          :date="chosenDate"
          @close="model = false"
        />
      </v-window-item>
    </v-window>
  </b-dialog>
</template>
